/* ═══════════════════════════════════════════════════════════════
   Abwicklung: aus einer gedrehten Flasche wird ein flaches Bild

   Die erste Fassung arbeitete Bild für Bild: Verschiebung gegen das
   Vorgängerbild messen, Streifen um die Mitte sofort in ein Sammelbild
   schreiben. Am echten Video (Flasche auf einem Deckel, von Hand in
   Schüben gedreht, Telefon frei gehalten) erkannte sie die Umdrehung
   nicht und lieferte ein verschwommenes, abgehacktes Etikett. Drei
   Gründe, und je eine Antwort:

   1. **Spiegelungen drehen sich nicht mit.** Auf dunklem Glas sind sie
      oft das Kräftigste im Bild und riefen bei jedem Vergleich „keine
      Drehung“ — am Video stand nach einer vollen Umdrehung gut ein
      Fünftel des Wegs. Antwort: Das feste Bild (Spiegelungen,
      Abschattung) wird nach der Aufnahme als Median über die Bilder
      geschätzt und abgezogen. Gemittelt nicht über die Zeit, sondern
      über den Drehwinkel — wo die Hand langsam dreht, stünde sonst
      das Etikett im Median und als Geisterschrift im festen Bild.
   2. **Fehler summieren sich.** Bild an Bild gereiht, wird jeder kleine
      Messfehler weitergetragen. Antwort: Gemessen wird zwischen vielen
      Bildpaaren mit Abständen von 1 bis 16 Bildern, und alle Winkel
      werden in einer Ausgleichsrechnung gemeinsam bestimmt — robust
      gewichtet, damit einzelne Fehlgriffe nichts verbiegen. Wie viele
      Spalten eine Umdrehung hat, zeigt danach die Textur selbst: Sie
      wiederholt sich nach genau einer Umdrehung.
   3. **Schärfe.** Streifen sofort zu schreiben heißt, sie mit dem
      vorläufigen Winkel zu platzieren; jeder Fehler wird zur Fuge.
      Antwort: Je Bild wird ein schmaler Streifen in voller Auflösung
      aufbewahrt und erst am Ende mit den ausgeglichenen Winkeln
      zusammengesetzt. Dazu die senkrechte Feinausrichtung: Jedes Bild
      wird an den Rändern gegen das gemeinsame Mosaik ausgerichtet,
      getrennt für Hals und Bauch — die Kamera wandert auch in der
      Höhe, und der Maßstab aus der Schulter ist auf 1–2 % genau,
      was am unteren Etikettrand schon mehrere Pixel ausmacht. Wo
      Anfang und Ende der Aufnahme aneinanderstoßen, wird der
      verbleibende Versatz eigens gemessen und ausgeglichen.

   Wo die Flasche im Bild steht, misst kern/flaschenlage.js. Hier
   beginnt die Arbeit in Zylinderkoordinaten:

   - **Messband** (je Bild, grob): Helligkeit über Höhe u (Hals und
     Bauch, nicht die schräge Schulter) × Winkel φ ∈ ±0,9 rad in
     Schritten von 0,01 rad. Für Winkel und Höhenlage.
   - **Streifen** (je Bild, voll aufgelöst, farbig): φ um die Mitte,
     so breit, wie sich die Flasche seit dem letzten Bild gedreht hat,
     alle Höhen von der Mündung bis zum Boden. Daraus entsteht das
     Ergebnis.

   Während der Aufnahme läuft nur eine grobe Winkelschätzung (für den
   Fortschritt, die Streifenbreite, das Auslassen von Bildern, während
   die Hand umgreift, und das Ende der Aufnahme); die genaue Rechnung
   folgt danach in `abwicklungAuswerten` — am Video mit 744 Bildern
   knapp 3 s am Rechner.

   Vorzeichen: Inhalt, der in Bild t bei Bandspalte φ liegt, liegt in
   Bild t' bei φ + (θ_t' − θ_t). θ zählt in Bandspalten (0,01 rad in
   den Abtastkoordinaten; durch die Perspektive ist eine Umdrehung
   etwas mehr als 628 davon — der Umfang L wird gemessen).

   Reine Logik ohne DOM. Tests in tests/abwicklung.test.mjs mit einer
   gerenderten Flasche; zusätzlich am echten Video geprüft (siehe
   UEBERGABE.md).
   ═══════════════════════════════════════════════════════════════ */

import { formVon, halsBeginn, kapselTiefste, koerperRadius } from "./flasche.js";
import {
  achseX, lageSuchen, lageVerfolgen, luminanz, profilAusForm, profilBilden, profilSammeln,
  profilSammler, rhoBei, umrissLinie,
} from "./flaschenlage.js";


const DPHI = 0.01;
const NPHI = 181;
const MITTE = 90;
const BAND_DU = 0.04;
/* Texturhöhe höchstens 1280 Zeilen: Die Streifen aller Bilder liegen
   bis zum Ende der Aufnahme im Speicher (am Video gut 20 MB bei 800
   Zeilen) — auf dem Telefon soll das unter 100 MB bleiben. */
const TEXTUR_MAX = 1280;
const TEXTUR_UEBERHANG = 1.25;
const L_NAEHERUNG = 2 * Math.PI / DPHI;
/* Bereich des Umfangs in Bandspalten: nie kleiner als 2π/Δφ — bei
   naher Kamera wandert die Mitte schneller durchs Bild, als der Umriss
   vermuten lässt (am Video 1,05-fach). */
const L_MIN = 0.95 * L_NAEHERUNG, L_MAX = 1.45 * L_NAEHERUNG;
const SIN = Float32Array.from({ length: NPHI }, (_, j) => Math.sin((j - MITTE) * DPHI));

const median = (liste) => {
  if (!liste.length) return null;
  const s = Float64Array.from(liste).sort();
  return s[Math.floor(s.length / 2)];
};


/* ═══ Vorschau ═════════════════════════════════════════════════ */

/** Neuer Lauf. Bis `aufnahmeStarten` wird nur gesucht, verfolgt und
    der Umriss vermessen. */
function abwicklungBeginnen(formSchluessel) {
  const vorlage = profilAusForm(formSchluessel);
  return {
    formSchluessel, vorlage, profil: vorlage, phase: "vorschau",
    sammler: profilSammler(vorlage), lage: null, vorschauBilder: 0, schwach: 0,
  };
}

const alsHelligkeit = (bild) => (bild.g ? bild : luminanz(bild));

/** Ein Vorschaubild. `vorgabe` = { cx, oben, S } aus dem eingeblendeten
    Umriss, in Pixeln des Übersichtsbilds. Liefert die gefundene Lage,
    die Umrisslinie zum Einblenden und ob gestartet werden kann. */
function vorschauSchritt(z, uebersicht, vorgabe) {
  const bild = alsHelligkeit(uebersicht);
  let guete = 0;
  if (!z.lage) {
    const gefunden = lageSuchen(bild, vorgabe, z.vorlage);
    if (!gefunden || gefunden.guete < 0.35) return { bereit: false, lage: null, umriss: null, guete: gefunden ? gefunden.guete : 0 };
    z.lage = gefunden.lage; guete = gefunden.guete;
  } else {
    /* Maßstab fest, solange nur die Vorlage bekannt ist: Ihre Schulter
       sitzt woanders als die echte, und Kantenpaare dort zögen den
       Maßstab — der gemessene Umriss würde gestreckt. */
    const r = lageVerfolgen(bild, z.lage, z.vorlage, { mitSchulter: false, massstabFest: true });
    guete = r.verloren ? 0 : r.guete;
    if (!r.verloren) z.lage = r.lage;
  }
  /* Bleibt die Erkennung schwach, neu suchen — vielleicht wurde die
     Flasche erst jetzt in den Umriss gestellt. */
  z.schwach = guete < 0.35 ? z.schwach + 1 : 0;
  if (z.schwach > 6) {
    z.lage = null; z.vorschauBilder = 0; z.schwach = 0; z.sammler = profilSammler(z.vorlage);
    return { bereit: false, lage: null, umriss: null, guete };
  }
  if (guete >= 0.35) { profilSammeln(z.sammler, bild, z.lage); z.vorschauBilder++; }
  return { bereit: z.vorschauBilder >= 8, lage: z.lage, umriss: umrissLinie(z.lage, z.vorlage), guete };
}


/* ═══ Aufnahme ═════════════════════════════════════════════════ */

/** Umriss festschreiben und die Aufnahme vorbereiten.
    `voll` = { faktor } — Übersichtspixel je Vollbildpixel. */
function aufnahmeStarten(z, { faktor = 1 } = {}) {
  if (!z.lage) throw new Error("Keine Flasche erkannt.");
  const profil = profilBilden(z.sammler, z.vorlage);
  const zeilen = [];
  const gruppen = [[], []];
  for (const [g, [u0, u1]] of [[0, profil.hals], [1, profil.bauch]]) {
    for (let u = u0; u <= u1 + 1e-9; u += BAND_DU) { gruppen[g].push(zeilen.length); zeilen.push(u); }
  }
  const uMittel = gruppen.map((idx) => idx.reduce((s, i) => s + zeilen[i], 0) / Math.max(1, idx.length));
  const S_voll = z.lage.S / faktor;
  /* Die Streifen reichen ein Viertel über den erwarteten Boden hinaus:
     Wo er im Bild liegt, zeigt erst der Umfang (siehe zusammensetzen). */
  const uTextur = profil.uBoden * TEXTUR_UEBERHANG;
  const texZeilen = Math.max(16, Math.min(TEXTUR_MAX, Math.round(uTextur * S_voll)));
  const duTex = uTextur / texZeilen;
  const dAlpha = duTex / profil.c;                /* rad je Texturspalte */
  Object.assign(z, {
    phase: "aufnahme", profil, faktor,
    zeilen: Float32Array.from(zeilen), gruppen, uMittel, nz: zeilen.length,
    texZeilen, duTex, dAlpha, kTex: DPHI / dAlpha,
    bilder: [],
    /* grobe Winkelschätzung */
    stillRun: null, schluessel: null, thetaSchluessel: 0, theta: 0, schritte: [], uebersprungen: 0, geschw: 0, stillstand: 0, bildNr: 0,
    verlorenInFolge: 0,
  });
  return { profil };
}


/* Messband eines Bilds (Helligkeit, 0–255). */
function bandAbtasten(z, bild, lage) {
  const { zeilen, nz, profil } = z;
  const { g, breite, hoehe } = bild;
  const band = new Uint8Array(nz * NPHI);
  for (let i = 0; i < nz; i++) {
    const u = zeilen[i];
    const y = lage.oben + u * lage.S;
    const a = achseX(lage, y);
    const r = rhoBei(profil, u) * lage.R;
    const y0 = Math.max(0, Math.min(hoehe - 2, Math.floor(y)));
    const fy = Math.max(0, Math.min(1, y - y0));
    for (let j = 0; j < NPHI; j++) {
      const x = a + r * SIN[j];
      const x0 = Math.max(0, Math.min(breite - 2, Math.floor(x)));
      const fx = Math.max(0, Math.min(1, x - x0));
      const o = y0 * breite + x0;
      const v = (g[o] * (1 - fx) + g[o + 1] * fx) * (1 - fy) + (g[o + breite] * (1 - fx) + g[o + breite + 1] * fx) * fy;
      band[i * NPHI + j] = v < 0 ? 0 : v > 255 ? 255 : v;
    }
  }
  return band;
}


/* Streifen in voller Auflösung: Texturzeilen × Spalten −w..w. */
function streifenAbtasten(z, lage, w, voll) {
  const { texZeilen, duTex, dAlpha, profil, faktor } = z;
  const sinW = Math.sin(w * dAlpha);
  const ax = new Float32Array(texZeilen), rr = new Float32Array(texZeilen), yy = new Float32Array(texZeilen);
  let xmin = Infinity, xmax = -Infinity, ymin = Infinity, ymax = -Infinity;
  for (let k = 0; k < texZeilen; k++) {
    const u = k * duTex;
    const yo = lage.oben + u * lage.S;
    ax[k] = achseX(lage, yo) / faktor;
    rr[k] = rhoBei(profil, u) * lage.R / faktor;
    yy[k] = yo / faktor;
    xmin = Math.min(xmin, ax[k] - rr[k] * sinW); xmax = Math.max(xmax, ax[k] + rr[k] * sinW);
    ymin = Math.min(ymin, yy[k]); ymax = Math.max(ymax, yy[k]);
  }
  const x0 = Math.max(0, Math.floor(xmin) - 1), y0 = Math.max(0, Math.floor(ymin) - 1);
  const x1 = Math.min(voll.breite - 1, Math.ceil(xmax) + 2), y1 = Math.min(voll.hoehe - 1, Math.ceil(ymax) + 2);
  if (x1 <= x0 + 1 || y1 <= y0 + 1) return null;
  const quelle = voll.lies(x0, y0, x1 - x0 + 1, y1 - y0 + 1);
  const { data: d, width: qb, height: qh } = quelle;
  const spalten = 2 * w + 1;
  const daten = new Uint8Array(texZeilen * spalten * 3);
  for (let j = -w; j <= w; j++) {
    const s = Math.sin(j * dAlpha);
    for (let k = 0; k < texZeilen; k++) {
      const x = ax[k] + rr[k] * s - x0, y = yy[k] - y0;
      const xi = Math.max(0, Math.min(qb - 2, Math.floor(x))), yi = Math.max(0, Math.min(qh - 2, Math.floor(y)));
      const fx = Math.max(0, Math.min(1, x - xi)), fy = Math.max(0, Math.min(1, y - yi));
      const o = (yi * qb + xi) * 4, ob = o + qb * 4;
      const ziel = (k * spalten + j + w) * 3;
      for (let c = 0; c < 3; c++) {
        daten[ziel + c] = (d[o + c] * (1 - fx) + d[o + 4 + c] * fx) * (1 - fy) + (d[ob + c] * (1 - fx) + d[ob + 4 + c] * fx) * fy;
      }
    }
  }
  return { w, daten };
}


/* Verschiebung s mit b(j) ≈ a(j − s) zwischen zwei Bändern (nz ×
   NPHI). Mit `still` werden beide vorher um das feste Bild bereinigt
   — zeilenweise beim Vergleich, damit nicht für jedes Bild ein
   zweites Band im Speicher liegen muss. Liefert { s, q, amRand }:
   Liegt das Minimum am Rand des Suchbereichs, ist es keins. */
const zeileA = new Float64Array(NPHI), zeileB = new Float64Array(NPHI);
function vergleicheRest(a, b, nz, von, bis, zeilenSchritt = 2, still = null) {
  const anzahl = bis - von + 1;
  const kosten = new Float64Array(anzahl);
  const zA = zeileA, zB = zeileB;
  let zeilen = 0;
  for (let z = 0; z < nz; z += zeilenSchritt) {
    const o = z * NPHI;
    if (still) for (let j = 0; j < NPHI; j++) { zA[j] = a[o + j] - still[o + j]; zB[j] = b[o + j] - still[o + j]; }
    else for (let j = 0; j < NPHI; j++) { zA[j] = a[o + j]; zB[j] = b[o + j]; }
    for (let i = 0; i < anzahl; i++) {
      const s = von + i;
      const j1 = s < 0 ? NPHI + s : NPHI;
      let j = s > 0 ? s : 0, summe = 0;
      /* von Hand vierfach entrollt: halbiert die Rechenzeit */
      for (; j + 3 < j1; j += 4) {
        const d0 = zB[j] - zA[j - s], d1 = zB[j + 1] - zA[j + 1 - s];
        const d2 = zB[j + 2] - zA[j + 2 - s], d3 = zB[j + 3] - zA[j + 3 - s];
        summe += d0 * d0 + d1 * d1 + d2 * d2 + d3 * d3;
      }
      for (; j < j1; j++) { const d = zB[j] - zA[j - s]; summe += d * d; }
      kosten[i] += summe;
    }
    zeilen++;
  }
  for (let i = 0; i < anzahl; i++) {
    const s = von + i;
    const breite = Math.min(NPHI, NPHI + s) - Math.max(0, s);
    kosten[i] = breite < NPHI * 0.4 ? Infinity : kosten[i] / (breite * zeilen);
  }
  const m = minimum(kosten, von);
  return { ...m, amRand: m.s <= von + 0.5 || m.s >= bis - 0.5 };
}

/* Minimum einer Kostenkurve mit Parabel-Verfeinerung und Güte
   (wie deutlich es aus dem Median ragt). */
function minimum(kosten, von) {
  let i = 0;
  for (let k = 1; k < kosten.length; k++) if (kosten[k] < kosten[i]) i = k;
  let off = 0;
  if (i > 0 && i < kosten.length - 1 && Number.isFinite(kosten[i - 1]) && Number.isFinite(kosten[i + 1])) {
    const l = kosten[i - 1], m = kosten[i], r = kosten[i + 1];
    const d = l - 2 * m + r;
    if (d > 0) off = Math.max(-0.5, Math.min(0.5, 0.5 * (l - r) / d));
  }
  const endl = Array.from(kosten).filter(Number.isFinite);
  const med = median(endl);
  const q = Number.isFinite(kosten[i]) ? (med - kosten[i]) / (kosten[i] + 1e-3) : 0;
  return { s: von + i + off, q, kosten: kosten[i] };
}


/** Ein Kamerabild während der Aufnahme. `uebersicht` ist das
    verkleinerte Bild (RGBA oder Helligkeit), `voll` = { breite, hoehe,
    lies(x, y, b, h) → RGBA } liefert Ausschnitte in voller Auflösung,
    `zeit` (ms, wenn bekannt) die Aufnahmezeit des Bilds — daraus folgt,
    wie weit sich die Flasche seit dem letzten Bild höchstens gedreht
    haben kann, falls das Telefon nicht jedes Bild schafft.
    Liefert den Stand für die Anzeige. */
function abwicklungSchritt(z, uebersicht, voll, zeit = null) {
  if (z.phase !== "aufnahme") throw new Error("Aufnahme nicht gestartet.");
  const bild = alsHelligkeit(uebersicht);
  const nr = z.bildNr++;
  let r = lageVerfolgen(bild, z.lage, z.profil);
  /* Verloren (Hand vor der Flasche, Ruck): ab dem dritten Bild neu
     suchen, um die letzte gute Lage herum */
  if (r.verloren && z.verlorenInFolge >= 2) {
    const gefunden = lageSuchen(bild, z.lage, z.profil);
    if (gefunden && gefunden.guete >= 0.5) r = lageVerfolgen(bild, gefunden.lage, z.profil);
  }
  if (r.verloren) {
    z.verlorenInFolge++;
    return stand(z, { verloren: true });
  }
  z.verlorenInFolge = 0;
  z.lage = r.lage;
  z.bildHoehe = bild.hoehe;
  const band = bandAbtasten(z, bild, z.lage);
  const vorher = z.theta;
  const dtGrob = zeit != null && z.letzteZeit != null ? Math.max(0, zeit - z.letzteZeit) / 1000 : null;
  z.letzteZeit = zeit;
  grobeDrehung(z, band, dtGrob);
  const schritt = Math.abs(z.theta - vorher);
  const tempo = zeit != null ? tempoMessen(z, zeit) : null;
  /* Steht die Flasche (die Hand greift um), bringt jedes weitere Bild
     nichts Neues — nur jedes sechste wird behalten. Ob sie steht,
     sagen zwei Zeichen zugleich: Die grobe Drehung ruht, und das
     Messband hat sich seit dem letzten behaltenen Bild kaum verändert
     (im Mittel unter einer Graustufe; gedreht wird es zwei und mehr).
     Die grobe Drehung allein kann hängen bleiben, wenn die Drehung
     aus dem Stand beginnt — dann gingen Bilder verloren. */
  const letztes = z.bilder[z.bilder.length - 1];
  const seitLetztem = letztes ? Math.abs(z.theta - letztes.theta) : Infinity;
  const aenderung = letztes ? bandAenderung(band, letztes.band) : Infinity;
  if (seitLetztem < 0.4 && aenderung < 1 && z.uebersprungen < 5) {
    z.uebersprungen++;
    return stand(z, { schritt });
  }
  z.uebersprungen = 0;
  z.schritte.push(letztes ? Math.min(seitLetztem, 60) : 0);
  if (z.schritte.length > 4) z.schritte.shift();
  /* Streifen so breit, wie sich die Flasche seit den letzten
     behaltenen Bildern gedreht hat — mit Reserve, denn die grobe
     Schätzung liegt mal darüber, mal darunter. */
  const maxSchritt = Math.max(...z.schritte) * z.kTex;
  let w = Math.max(aenderung > 2 ? 6 : 4, Math.ceil(1.8 * maxSchritt + 3));
  /* Dreht sie sich, mindestens so breit, wie eine zügige Hand (zwei
     Bogenmaß je Sekunde) seit dem letzten Bild drehen kann — die grobe
     Schätzung misst bei ausgelassenen Bildern zu kurz */
  const dt = zeit != null && letztes && letztes.zeit != null ? Math.max(0, zeit - letztes.zeit) / 1000 : null;
  if (dt != null && aenderung > 2) w = Math.max(w, Math.ceil(0.6 * DREH_MAX * dt / z.dAlpha));
  w = Math.min(48, w);
  const streifen = voll ? streifenAbtasten(z, z.lage, w, voll) : null;
  z.bilder.push({ nr, zeit, lage: { ...z.lage }, band, streifen, theta: z.theta });
  /* Zu schnell: über anderthalb Bogenmaß je Sekunde verwischt das
     Etikett schon bei 1/60 s Belichtung um mehrere Pixel. Ohne Zeit
     gilt die Schrittweite (sechs Spalten bei 30 Bildern je Sekunde) —
     ein Telefon, das nur zehn Bilder schafft, hieße sonst schon eine
     gemächliche Hand zu schnell. */
  const zuSchnell = tempo != null ? tempo > 1.5 : schritt > 6;
  return stand(z, { schritt, zuSchnell });
}


/* Drehtempo (Bogenmaß je Sekunde) über die letzte halbe Sekunde —
   über einzelne Bilder gemessen, gälte schon das Aufholen der groben
   Schätzung nach einem ausgelassenen Bild als zu schnell. */
function tempoMessen(z, zeit) {
  const v = z.tempoVerlauf || (z.tempoVerlauf = []);
  v.push([zeit, z.theta]);
  while (v.length > 2 && zeit - v[1][0] >= 500) v.shift();
  const [t0, theta0] = v[0];
  return zeit - t0 >= 150 ? Math.abs(z.theta - theta0) * DPHI / ((zeit - t0) / 1000) : 0;
}

/* Mittlere Helligkeitsänderung zwischen zwei Messbändern (jeder
   dritte Punkt genügt). */
function bandAenderung(a, b) {
  let summe = 0, n = 0;
  for (let i = 0; i < a.length; i += 3) { summe += Math.abs(a[i] - b[i]); n++; }
  return summe / n;
}


/* Grobe Drehung während der Aufnahme: Vergleich mit einem
   Schlüsselbild, beide um ein laufendes Median-Bild bereinigt. Das
   Schlüsselbild bleibt, bis die Flasche gut 40 Spalten weiter ist:
   Kleine Verschiebungen zieht der Rest der Spiegelungen zur Null (am
   Video maß die Kette aus Nachbarbildern ein Viertel zu kurz), große
   kaum. Für Fortschritt, Streifenbreite und das Auslassen stehender
   Bilder genügt das; die genaue Rechnung folgt nach der Aufnahme. */
const SCHLUESSEL_ABSTAND = 40;
/* Schnellste Drehung, mit der gerechnet wird (rad/s): Am Video drehte
   die Hand in Schüben bis knapp 1,8 rad/s. */
const DREH_MAX = 2;

function grobeDrehung(z, band, dt = null) {
  const n = band.length;
  if (!z.stillRun) {
    z.stillRun = Float32Array.from(band);
    z.schluessel = Float32Array.from(band);
    z.thetaSchluessel = 0;
    z.nSchluessel = 0;
    z.nGrob = 0;
    return;
  }
  /* laufender Median: je Bild höchstens zwei Stufen */
  const S = z.stillRun;
  for (let i = 0; i < n; i++) {
    const d = band[i] - S[i];
    S[i] += d > 1 ? 2 : d < -1 ? -2 : d;
  }
  /* Beide Bänder werden um das jetzige feste Bild bereinigt — das
     Schlüsselbild ist roh gespeichert, sonst stünde in ihm das feste
     Bild von damals. In den ersten Bildern kennt der laufende Median
     das feste Bild noch nicht (er beginnt beim ersten Band — dann
     bliebe vom Schlüsselbild nichts übrig, und die Vergleiche träfen
     Zufall); so lange roh. */
  z.nGrob++;
  const still = z.nGrob > 12 ? S : null;
  /* Suchfenster um die Fortschreibung: so weit, wie die Flasche seit
     dem letzten Bild gekommen sein kann — nach dem Tempo und, wenn die
     Zeit bekannt ist, nach der schnellsten Hand. Liegt der Treffer am
     Rand, noch einmal weit. */
  const erwartet = z.theta - z.thetaSchluessel + z.geschw;
  const f = Math.min(40, Math.max(6 + Math.ceil(0.75 * Math.abs(z.geschw)), dt != null ? Math.ceil(0.8 * DREH_MAX * dt / DPHI) + 4 : 0));
  let t = vergleicheRest(z.schluessel, band, z.nz, Math.floor(erwartet) - f, Math.ceil(erwartet) + f, 3, still);
  let verloren = false;
  if (t.amRand) {
    const mitte = z.theta - z.thetaSchluessel;
    t = vergleicheRest(z.schluessel, band, z.nz, Math.floor(mitte) - 30, Math.ceil(mitte) + 30, 3, still);
    if (t.q < 0.5) { t = { ...t, q: 0 }; verloren = true; }
  }
  /* Ohne deutliches Minimum (Pause, glattes Glas) nicht dem Zufall
     folgen, sondern mit nachlassendem Tempo fortschreiben. Über langem
     Glas misst die Schätzung dann zu kurz — das verlängert nur die
     Aufnahme. Das Tempo beizubehalten, ließ sie am Video bis 17 % zu
     lang messen; dann reichte die Aufnahme nicht ganz herum. */
  const neu = t.q >= 0.2 ? z.thetaSchluessel + t.s : z.theta + 0.5 * z.geschw;
  z.geschw = 0.5 * z.geschw + 0.5 * (neu - z.theta);
  z.theta = neu;
  z.nSchluessel++;
  /* Neues Schlüsselbild nach 40 Spalten — oder sofort, wenn der
     Vergleich die Spur verloren hat: Sonst entfernte sich die Flasche
     immer weiter vom Suchfenster, und die Schätzung bliebe stehen. */
  if (verloren || Math.abs(z.theta - z.thetaSchluessel) > SCHLUESSEL_ABSTAND || z.nSchluessel > 60) {
    z.schluessel.set(band); z.thetaSchluessel = z.theta; z.nSchluessel = 0;
  }
}


/* Wann die Aufnahme genug hat: nach 1,2 Umdrehungen der groben
   Schätzung. Die Auswertung braucht Überlappung, um Anfang und Ende
   zusammenzufügen. Die grobe Schätzung misst eher zu kurz (am Video
   3 bis 20 %, mehr, wenn das Telefon Bilder auslässt) — dann wird
   etwas länger gedreht; zu lang gemessen hat sie am Video nicht.
   Den Umlauf schon während der Aufnahme zu erkennen, klappte nicht
   verlässlich: Ohne gutes festes Bild trafen die Vergleiche oft
   Spiegelungsreste. */
const ZIEL = 1.2 * L_NAEHERUNG;

function stand(z, extra = {}) {
  const weg = Math.abs(z.theta);
  if (extra.schritt != null) z.stillstand = extra.schritt < 0.1 ? z.stillstand + 1 : 0;
  return {
    fortschritt: Math.min(1, weg / ZIEL),
    fertig: weg >= ZIEL,
    steht: z.bilder.length > 20 && z.stillstand > 30,
    zuSchnell: false,
    verloren: false,
    ...extra,
  };
}


/* ═══ Auswertung ═══════════════════════════════════════════════ */

const warte = () => new Promise((ok) => setTimeout(ok, 0));

/** Nach der Aufnahme: Winkel, Umfang und Höhenlage ausgleichen,
    Streifen zusammensetzen. `pause` gibt der Oberfläche zwischen den
    Schritten Luft; `beiFortschritt(anteil)` meldet den Stand. */
async function abwicklungAuswerten(z, { pause = warte, beiFortschritt = () => {}, protokoll = () => {} } = {}) {
  const bilder = z.bilder;
  const n = bilder.length;
  if (n < 20) return { fehler: "zuKurz" };
  const nz = z.nz;
  const bands = bilder.map((b) => b.band);

  /* 1. Ausgangspunkt ist die grobe Schätzung aus der Aufnahme. Eine
     eigene erste Runde aus Nachbarpaaren gegen den Zeitmedian war
     schlechter: Wo ein Etikett die halbe Flasche umfasst, steht es im
     Zeitmedian, und jeder Vergleich wird zur Null gezogen — am Video
     ein Viertel zu kurz, bei ausgelassenen Bildern bis zur Hälfte.
     Die grobe Schätzung vergleicht über 40 Spalten und hält. */
  let theta = Float64Array.from(bilder.map((b) => b.theta));
  protokoll("online", theta);
  let still = festesBild(bands);
  beiFortschritt(0.05); await pause();
  /* Die grobe Schätzung geht außerdem mit kleinem Gewicht in jede
     Ausgleichsrechnung ein: Sie hält die Kette zusammen, falls an
     einer Stelle kein Paar trägt (Flasche kurz verloren, Ruck). */
  const vorwissen = [];
  for (let t = 0; t + 1 < n; t++) vorwissen.push([t, t + 1, theta[t + 1] - theta[t], 0.05]);
  const GROB_ZEILEN = 3;
  /* Suchfenster der Nachbarpaare um null: mindestens 8 + 4k Spalten,
     bei schneller Drehung (oder wenn das Telefon nicht jedes Bild
     schafft) nach der groben Schätzung und der Zeit weiter. Ein Paar,
     dessen wahre Verschiebung außerhalb liegt, fände ein falsches
     Minimum. */
  const online = Float64Array.from(theta);
  const zeiten = bilder.every((b) => b.zeit != null) ? bilder.map((b) => b.zeit) : null;
  const grobFenster = (e, k, g, dt) => Math.max(8 + 4 * k, Math.ceil(1.5 * g) + 6,
    dt != null ? Math.ceil(0.8 * DREH_MAX * dt / 1000 / DPHI) + 6 : 0);
  let mess;
  protokoll("runde1", theta);
  /* 2. Festes Bild richtig trennen und die Nachbarn noch einmal: Der
     Median über die Zeit enthält Etikett, wo ein Etikett die halbe
     Flasche umfasst — dann steht im „festen Bild“ ein Etikettrand,
     der jeden Vergleich zur Verschiebung null zieht. */
  still = schichtenTrennen(bands, theta, nz, still);
  mess = paarMessungen(bands, still, nz, null, { fenster: grobFenster, abstaende: [1, 2], zeilenSchritt: GROB_ZEILEN, grob: online, zeiten });
  theta = ausgleich(n, [...mess, ...vorwissen], theta);
  protokoll("runde1b", theta);
  beiFortschritt(0.3); await pause();
  /* 3. Fein: alle Abstände, gesucht um die Vorhersage, mit neu
     getrenntem festem Bild. Eine weitere Runde mit engerem Fenster
     brachte am Video nichts mehr (eher weniger). */
  still = schichtenTrennen(bands, theta, nz, still, 1);
  mess = paarMessungen(bands, still, nz, theta, { fenster: (e) => 2 + Math.ceil(0.25 * Math.abs(e)), zeilenSchritt: 2 });
  theta = ausgleich(n, [...mess, ...vorwissen], theta);
  protokoll("runde2", theta);
  beiFortschritt(0.45); await pause();

  /* 4. Umfang: aus der Textur selbst, nicht aus dem Messband */
  const umfang = umfangMessen(z, theta);
  protokoll("umfang", umfang && { L: umfang.L, guete: umfang.guete, ueberlappung: umfang.ueberlappung });
  if (!umfang) return { fehler: "keinUmlauf" };
  const L = umfang.L;
  /* Für die Höhen zählt die Drehrate am Bauch allein (dort ist der
     Umriss verlässlich gemessen; siehe hoehenAbbildung) */
  const bauchAnteil = bauchRate(z, bands, still, theta, mess);
  protokoll("bauchAnteil", bauchAnteil);
  beiFortschritt(0.65); await pause();

  /* 5. Senkrechte Feinausrichtung */
  const hoehen = hoehenAusrichten(z, bands, still, theta, L);
  protokoll("vn", hoehen.vn); protokoll("vb", hoehen.vb);
  beiFortschritt(0.85); await pause();

  /* 6. Zusammensetzen */
  const ergebnis = zusammensetzen(z, theta, L, hoehen, L * bauchAnteil);
  beiFortschritt(1);
  return { ...ergebnis, theta: Array.from(theta), bilder: n, verarbeitet: z.bildNr };
}


/* Erste Schätzung des festen Bilds: Median je Bandpunkt über bis zu
   150 Bilder, gleichmäßig über die Aufnahme verteilt. Gut genug für
   die erste Runde; wo ein Etikett die halbe Flasche umfasst, steht
   allerdings Etikett darin — das richtet schichtenTrennen. */
function festesBild(bands) {
  const n = bands.length, groesse = bands[0].length;
  const auswahl = [];
  const ziel = Math.min(n, 150);
  for (let k = 0; k < ziel; k++) auswahl.push(Math.min(n - 1, Math.floor((k + 0.5) * n / ziel)));
  const S = new Float32Array(groesse);
  const zaehler = new Uint16Array(256);
  const halb = auswahl.length / 2;
  for (let p = 0; p < groesse; p++) {
    zaehler.fill(0);
    for (let k = 0; k < auswahl.length; k++) zaehler[bands[auswahl[k]][p]]++;
    let acc = 0, v = 0;
    for (; v < 256; v++) { acc += zaehler[v]; if (acc >= halb) break; }
    S[p] = v;
  }
  return S;
}


/* Festes Bild und Haut trennen: Jedes Messband ist die Haut (um θ
   verschoben) plus das feste Bild. Abwechselnd wird die Haut als
   Mittel der bereinigten Bänder an ihrer Stelle geschätzt und das
   feste Bild als Median dessen, was nach Abzug der Haut bleibt. Die
   Haut liegt dabei durchgehend (ohne Umbruch), der Umfang wird hier
   noch nicht gebraucht. `start`: erste Schätzung des festen Bilds. */
function schichtenTrennen(bands, theta, nz, start, runden = 2) {
  const n = bands.length, G = nz * NPHI;
  let tmin = Infinity, tmax = -Infinity;
  for (const t of theta) { tmin = Math.min(tmin, t); tmax = Math.max(tmax, t); }
  const a0 = Math.floor(-tmax) - 1, W = Math.ceil(NPHI - 1 - tmin) + 2 - a0;
  const M = new Float32Array(nz * W), gew = new Float32Array(nz * W);
  let S = Float32Array.from(start);
  /* Auswahl für den Median: gleichmäßig über den Winkel */
  const reihenfolge = Array.from(theta.keys()).sort((p, q) => theta[p] - theta[q]);
  const auswahl = [];
  const ziel = Math.min(n, 150);
  for (let k = 0; k < ziel; k++) {
    const i = reihenfolge[Math.min(n - 1, Math.floor((k + 0.5) * n / ziel))];
    if (!auswahl.includes(i)) auswahl.push(i);
  }
  const m = auswahl.length, werte = new Float32Array(m);
  const versaetze = auswahl.map((t) => -theta[t] - a0);
  for (let runde = 0; runde < runden; runde++) {
    M.fill(0); gew.fill(0);
    /* Für die Haut genügt jedes zweite Bild */
    for (let t = 0; t < n; t += 2) {
      const b = bands[t];
      const versatz = -theta[t] - a0;
      const ai = Math.floor(versatz), f = versatz - ai;
      for (let r = 0; r < nz; r++) {
        const o = r * NPHI, om = r * W + ai;
        for (let j = 0; j < NPHI; j++) {
          const v = b[o + j] - S[o + j];
          M[om + j] += (1 - f) * v; gew[om + j] += 1 - f;
          M[om + j + 1] += f * v; gew[om + j + 1] += f;
        }
      }
    }
    for (let p = 0; p < M.length; p++) M[p] = gew[p] > 1e-6 ? M[p] / gew[p] : 0;
    /* Median des Rests je Bandpunkt */
    for (let r = 0; r < nz; r++) {
      for (let j = 0; j < NPHI; j++) {
        const p = r * NPHI + j;
        for (let q = 0; q < m; q++) {
          const v = versaetze[q] + j, ai = Math.floor(v), f = v - ai, om = r * W + ai;
          werte[q] = bands[auswahl[q]][p] - (M[om] * (1 - f) + M[om + 1] * f);
        }
        S[p] = mitteWert(werte, m);
      }
    }
  }
  return S;
}


/* Median eines Felds (wird umsortiert): Hoare-Auswahl. */
function mitteWert(a, n) {
  const k = n >> 1;
  let lo = 0, hi = n - 1;
  while (lo < hi) {
    const pivot = a[(lo + hi) >> 1];
    let i = lo, j = hi;
    while (i <= j) {
      while (a[i] < pivot) i++;
      while (a[j] > pivot) j--;
      if (i <= j) { const x = a[i]; a[i] = a[j]; a[j] = x; i++; j--; }
    }
    if (k <= j) hi = j; else if (k >= i) lo = i; else break;
  }
  return a[k];
}


/* Paarvergleiche über die Abstände 1, 2, 4, 8, 16 mit Suchfenster um
   die Vorhersage. */
function paarMessungen(bands, still, nz, theta, { fenster, abstaende = [1, 2, 4, 8, 16], zeilenSchritt = 1, grob = null, zeiten = null }) {
  const n = bands.length;
  const mess = [];
  for (const k of abstaende) {
    for (let t = 0; t + k < n; t++) {
      const e = theta ? theta[t + k] - theta[t] : 0;
      const f = Math.min(80, fenster(e, k, grob ? Math.abs(grob[t + k] - grob[t]) : 0, zeiten ? zeiten[t + k] - zeiten[t] : null));
      const { s, q, amRand } = vergleicheRest(bands[t], bands[t + k], nz, Math.floor(e) - f, Math.ceil(e) + f, zeilenSchritt, still);
      if (!amRand) mess.push([t, t + k, s, q]);
    }
  }
  return mess;
}


/* ── Ausgleich ────────────────────────────────────────────────
   Unbekannte θ_0..θ_{n−1}, Messungen θ_b − θ_a = s, θ_0 = 0.
   Gewichte aus der Güte, dann robust nachgewichtet (Ausreißer
   verlieren quadratisch an Gewicht). Gelöst mit vorkonditionierten
   konjugierten Gradienten auf den Normalgleichungen — die Matrix
   wird nie aufgestellt. */
function ausgleich(n, mess, start = null) {
  const gl = mess.map(([a, b, s, q]) => ({ a, b, s, q: Math.max(0.05, Math.min(5, q)) }));
  let x = new Float64Array(n);
  if (start) for (let i = 0; i < n; i++) x[i] = start[i] - start[0];
  const w = Float64Array.from(gl, (e) => e.q);
  for (let runde = 0; runde < 6; runde++) {
    x = cgLoesen(n, gl, w, x);
    const res = gl.map((e) => Math.abs(x[e.b] - x[e.a] - e.s));
    const sig = 1.4826 * median(res) + 0.1;
    for (let i = 0; i < gl.length; i++) {
      const r = res[i] / (2.5 * sig);
      w[i] = gl[i].q / Math.max(1, r * r);
    }
  }
  return x;
}

function cgLoesen(n, gl, w, x0) {
  const ANKER = 1e3;
  const mult = (v, aus) => {
    aus.fill(0);
    for (let i = 0; i < gl.length; i++) {
      const e = gl[i];
      const wr = w[i] * (v[e.b] - v[e.a]);
      aus[e.b] += wr; aus[e.a] -= wr;
    }
    aus[0] += ANKER * v[0];
    return aus;
  };
  const rechts = new Float64Array(n), diag = new Float64Array(n).fill(1e-9);
  for (let i = 0; i < gl.length; i++) {
    const e = gl[i];
    rechts[e.b] += w[i] * e.s; rechts[e.a] -= w[i] * e.s;
    diag[e.b] += w[i]; diag[e.a] += w[i];
  }
  diag[0] += ANKER;
  const x = Float64Array.from(x0);
  const r = new Float64Array(n), p = new Float64Array(n), Ap = new Float64Array(n), zz = new Float64Array(n);
  mult(x, Ap);
  let rz = 0, norm = 0;
  for (let i = 0; i < n; i++) {
    r[i] = rechts[i] - Ap[i]; zz[i] = r[i] / diag[i]; p[i] = zz[i];
    rz += r[i] * zz[i]; norm += rechts[i] * rechts[i] / diag[i];
  }
  for (let it = 0; it < 4 * n + 50 && rz > 1e-20 * (norm + 1); it++) {
    mult(p, Ap);
    let pAp = 0;
    for (let i = 0; i < n; i++) pAp += p[i] * Ap[i];
    if (!(pAp > 0)) break;
    const alpha = rz / pAp;
    let rzNeu = 0;
    for (let i = 0; i < n; i++) {
      x[i] += alpha * p[i]; r[i] -= alpha * Ap[i];
      zz[i] = r[i] / diag[i]; rzNeu += r[i] * zz[i];
    }
    const beta = rzNeu / rz;
    rz = rzNeu;
    for (let i = 0; i < n; i++) p[i] = zz[i] + beta * p[i];
  }
  return x;
}


/* ── Umfang ───────────────────────────────────────────────────
   Wie viele Spalten eine Umdrehung hat, zeigt die Textur selbst: Die
   Streifen werden mit den Winkeln der Kette einmal durchgehend gemalt
   (ohne Umbruch, Anfang und Ende der Aufnahme liegen dann eine
   Umdrehung auseinander), und gesucht wird die Verschiebung P, um die
   sich das Bild wiederholt. Im Messband ginge das nicht verlässlich:
   Dort stehen Spiegelungsreste, die bei Verschiebung null zu sich
   selbst passen — am Video fanden die meisten Vergleiche zwischen
   Anfang und Ende genau diesen Fehltreffer. In der gemalten Textur
   ist eine feste Spiegelung ein waagerechter Streifen, der zu jeder
   Verschiebung gleich gut passt und nichts verfälscht.

   Verglichen wird die Helligkeit in Blöcken zu vier Zeilen, damit ein
   leichter Höhenversatz zwischen Anfang und Ende nicht stört; den
   Rest misst später das Zusammensetzen an der Fuge. Null, wenn sich
   keine deutliche Wiederholung mit mindestens 12 Spalten Überlappung
   findet (zu kurz gedreht). */
const UMFANG_UEBERLAPPUNG = 12;

function umfangMessen(z, theta) {
  const { bilder, kTex, texZeilen: H } = z;
  const n = bilder.length;
  const mitte = Float64Array.from(theta, (t) => -t * kTex);
  const schritte = Float64Array.from(theta, (_, t) => Math.abs(mitte[Math.min(n - 1, t + 1)] - mitte[Math.max(0, t - 1)]) / 2);
  let amin = Infinity, amax = -Infinity;
  for (let t = 0; t < n; t++) {
    if (!bilder[t].streifen) continue;
    amin = Math.min(amin, mitte[t]); amax = Math.max(amax, mitte[t]);
  }
  const von = Math.floor(amin) - 2, W = Math.ceil(amax) + 3 - von;
  const pMin = Math.ceil(L_MIN * kTex), pMax = Math.min(Math.floor(L_MAX * kTex), W - UMFANG_UEBERLAPPUNG);
  if (pMax <= pMin + 2) return null;
  const null0 = new Float64Array(n);
  const { acc, ws } = malen({ z, bilder, mitte, schritte, hoehen: { vn: null0, vb: null0 }, H, korrektur: null }, von, W, false);
  const BL = 4, HB = Math.floor(H / BL);
  const E = new Float32Array(HB * W).fill(NaN);
  for (let r = 0; r < HB; r++) {
    for (let c = 0; c < W; c++) {
      let s = 0, g = 0;
      for (let k = r * BL; k < r * BL + BL; k++) {
        const o = k * W + c;
        if (ws[o] > 0.05) { s += (acc[o * 3] * 0.299 + acc[o * 3 + 1] * 0.587 + acc[o * 3 + 2] * 0.114) / ws[o]; g++; }
      }
      if (g === BL) E[r * W + c] = s / BL;
    }
  }
  /* Je Verschiebung die normierte Korrelation der Überlappung, je
     Zeile um ihren Mittelwert bereinigt: Waagerechte Streifen (feste
     Spiegelungen, Kapsel, Glas) sind dann weg, es zählt nur, was sich
     entlang des Umfangs ändert — Schrift, Etikettkanten. */
  const korr = new Float64Array(pMax - pMin + 1).fill(-1);
  for (let P = pMin; P <= pMax; P++) {
    let sab = 0, saa = 0, sbb = 0;
    for (let r = 0; r < HB; r++) {
      const o = r * W;
      let ma = 0, mb = 0, m = 0;
      for (let c = 0; c + P < W; c++) {
        const a = E[o + c], b = E[o + c + P];
        if (a === a && b === b) { ma += a; mb += b; m++; }
      }
      if (m < UMFANG_UEBERLAPPUNG) continue;
      ma /= m; mb /= m;
      for (let c = 0; c + P < W; c++) {
        const a = E[o + c] - ma, b = E[o + c + P] - mb;
        if (a === a && b === b) { sab += a * b; saa += a * a; sbb += b * b; }
      }
    }
    if (saa > 0 && sbb > 0) korr[P - pMin] = sab / Math.sqrt(saa * sbb);
  }
  let i = 0;
  for (let k = 1; k < korr.length; k++) if (korr[k] > korr[i]) i = k;
  /* Zweitbeste Stelle: höchster Wert jenseits des Hügels um den
     Gipfel (bis dorthin, wo es zu beiden Seiten wieder ansteigt) —
     wie breit der Hügel ist, hängt an der Texturauflösung. */
  let links = i, rechts = i;
  while (links > 0 && korr[links - 1] <= korr[links]) links--;
  while (rechts < korr.length - 1 && korr[rechts + 1] <= korr[rechts]) rechts++;
  let zweit = -1;
  for (let k = 0; k < korr.length; k++) if (k < links || k > rechts) zweit = Math.max(zweit, korr[k]);
  let off = 0;
  if (i > 0 && i < korr.length - 1) {
    const l = korr[i - 1], m = korr[i], r = korr[i + 1], d = l - 2 * m + r;
    if (d < 0) off = Math.max(-0.5, Math.min(0.5, 0.5 * (l - r) / d));
  }
  const P = pMin + i + off, q = korr[i];
  /* deutlich: hohe Korrelation, klar vor der nächstbesten Stelle,
     nicht am Rand des Suchbereichs */
  if (!(q >= 0.5 && q - zweit >= 0.15) || i === 0 || i === korr.length - 1) return null;
  return { L: P / kTex, guete: q, abstand: q - zweit, ueberlappung: W - P };
}


/* ── Höhe: jedes Bild an den Rändern gegen das Mosaik ─────────── */
function bandMosaik(z, bands, still, theta, L, vn, vb) {
  const { nz, zeilen, uMittel } = z;
  const nL = Math.round(L);
  const acc = new Float32Array(nz * nL), ws = new Float32Array(nz * nL);
  const n = bands.length;
  for (let t = 0; t < n; t++) {
    const schritt = Math.abs((theta[Math.min(n - 1, t + 1)] - theta[Math.max(0, t - 1)]) / 2);
    const sig = Math.max(0.8, 0.6 * schritt);
    const breite = Math.ceil(3 * sig) + 1;
    for (let j = MITTE - breite; j <= MITTE + breite; j++) {
      const phi = j - MITTE;
      const w = Math.exp(-0.5 * (phi / sig) ** 2);
      const a = (((Math.round(phi - theta[t]) % nL) + nL) % nL);
      for (let i = 0; i < nz; i++) {
        const v = vz(zeilen[i], uMittel, vn[t], vb[t]);
        const zq = i + v;
        const z0 = Math.max(0, Math.min(nz - 2, Math.floor(zq))), f = Math.max(0, Math.min(1, zq - z0));
        const p0 = z0 * NPHI + j, p1 = p0 + NPHI;
        const wert = (bands[t][p0] - still[p0]) * (1 - f) + (bands[t][p1] - still[p1]) * f;
        acc[i * nL + a] += w * wert; ws[i * nL + a] += w;
      }
    }
  }
  for (let p = 0; p < acc.length; p++) acc[p] = ws[p] > 1e-6 ? acc[p] / ws[p] : 0;
  return { M: acc, nL, gewicht: ws };
}

/* Senkrechte Verschiebung (Bandzeilen) bei Höhe u, linear zwischen den
   Mitten von Hals- und Bauchgruppe. */
const vz = (u, uMittel, vn, vb) => vn + (vb - vn) * (u - uMittel[0]) / (uMittel[1] - uMittel[0]);

function hoehenAusrichten(z, bands, still, theta, L, runden = 2) {
  const n = bands.length, { nz, gruppen, zeilen, uMittel } = z;
  const vn = new Float64Array(n), vb = new Float64Array(n);
  /* Randspalten (15 bis 85 Spalten neben der Mitte), jede zweite */
  const spalten = [];
  for (let j = 0; j < NPHI; j += 2) if (Math.abs(j - MITTE) >= 15 && Math.abs(j - MITTE) <= 85) spalten.push(j);
  const J = spalten.length;
  const DV = [];
  for (let dv = -3; dv <= 3.001; dv += 0.5) DV.push(dv);
  const mh = new Float32Array(nz * J), rest = new Float32Array(nz * J);
  for (let runde = 0; runde < runden; runde++) {
    const { M, nL } = bandMosaik(z, bands, still, theta, L, vn, vb);
    const mess = [new Float64Array(n), new Float64Array(n)], gew = [new Float64Array(n), new Float64Array(n)];
    for (let t = 0; t < n; t += 2) {
      /* Mosaik an den Winkeln dieses Bilds (waagerecht schon
         interpoliert) und der bereinigte Bildrest — einmal je Bild,
         nicht je Versuch */
      for (let jj = 0; jj < J; jj++) {
        const al = spalten[jj] - MITTE - theta[t];
        const a0 = Math.floor(al), fa = al - a0;
        const c0 = ((a0 % nL) + nL) % nL, c1 = (c0 + 1) % nL;
        for (let r = 0; r < nz; r++) {
          mh[r * J + jj] = M[r * nL + c0] * (1 - fa) + M[r * nL + c1] * fa;
          const p = r * NPHI + spalten[jj];
          rest[r * J + jj] = bands[t][p] - still[p];
        }
      }
      for (let g = 0; g < 2; g++) {
        const idx = gruppen[g];
        if (idx.length < 4) continue;
        const kosten = new Float64Array(DV.length);
        for (let d = 0; d < DV.length; d++) {
          let summe = 0, cnt = 0;
          for (let k = 0; k < idx.length; k += 2) {
            const i = idx[k];
            const mz = i - vz(zeilen[i], uMittel, vn[t], vb[t]) + DV[d];
            const z0 = Math.floor(mz), f = mz - z0;
            if (z0 < 0 || z0 >= nz - 1) continue;
            const o0 = z0 * J, o1 = o0 + J, oi = i * J;
            for (let jj = 0; jj < J; jj++) {
              const e = rest[oi + jj] - (mh[o0 + jj] * (1 - f) + mh[o1 + jj] * f);
              summe += e * e;
            }
            cnt += J;
          }
          kosten[d] = cnt ? summe / cnt : Infinity;
        }
        const { s, q } = minimum(kosten, 0);
        mess[g][t] = -DV[0] - 0.5 * s;
        gew[g][t] = Math.max(0, Math.min(3, q));
      }
    }
    for (let g = 0; g < 2; g++) {
      /* ungerade Bilder: Nachbarn */
      for (let t = 1; t < n; t += 2) {
        const t2 = Math.min(n - 1, t + 1);
        mess[g][t] = (mess[g][t - 1] + mess[g][t2]) / 2; gew[g][t] = Math.min(gew[g][t - 1], gew[g][t2]) * 0.5;
      }
      const d = glaetten(mess[g], gew[g], 30);
      let sw = 0, sm = 0;
      for (let t = 0; t < n; t++) { sw += gew[g][t] + 1e-3; sm += (gew[g][t] + 1e-3) * d[t]; }
      const mittel = sm / sw;
      const v = g === 0 ? vn : vb;
      for (let t = 0; t < n; t++) v[t] += d[t] - mittel;
    }
  }
  return { vn, vb };
}

/* min Σ q (x − v)² + λ Σ (x_{t+1} − x_t)²: tridiagonal (Thomas). */
function glaetten(v, q, lam) {
  const n = v.length;
  const a = new Float64Array(n), b = new Float64Array(n), c = new Float64Array(n), d = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const qi = q[i] + 1e-3;
    b[i] = qi + (i > 0 ? lam : 0) + (i < n - 1 ? lam : 0);
    a[i] = i > 0 ? -lam : 0; c[i] = i < n - 1 ? -lam : 0;
    d[i] = qi * v[i];
  }
  for (let i = 1; i < n; i++) {
    const m = a[i] / b[i - 1];
    b[i] -= m * c[i - 1]; d[i] -= m * d[i - 1];
  }
  const x = new Float64Array(n);
  x[n - 1] = d[n - 1] / b[n - 1];
  for (let i = n - 2; i >= 0; i--) x[i] = (d[i] - c[i] * x[i + 1]) / b[i];
  return x;
}


/* ── Zusammensetzen ───────────────────────────────────────────
   Jede Texturspalte stammt aus genau einem Umlauf: Gewählt wird ein
   Fenster von einer Umdrehung Länge im durchgehenden Winkel; die
   Fuge (Ende an Anfang) kommt dorthin, wo im Überlappungsbereich am
   wenigsten zu sehen ist, und wird über wenige Spalten überblendet.
   Ohne diese Wahl stünden Anfang und Ende übereinander — schon ein
   Pixel Versatz ergäbe Doppelschrift.

   An der Fuge treffen Anfang und Ende der Aufnahme aufeinander; was
   dort an Versatz bleibt (Bruchteile einer Spalte aus dem Umfang,
   ein, zwei Zeilen aus der Höhe), wird eigens gemessen: Die Fuge wird
   zweimal gemalt, einmal nur aus dem Anfang, einmal nur aus dem Ende,
   und der Versatz zwischen beiden wird über den ganzen Umlauf verteilt
   ausgeglichen. Fängt die Aufnahme — wie meist — mit dem Etikett an,
   liegt die Fuge mitten in der Schrift; dort fiele jeder Rest auf.

   Danach wird die fertige Textur so gedreht, dass ihre Kante im
   leersten Glas liegt; wo das größte Etikett sitzt, gibt `vorne` an
   (Anteil 0..1), damit die 3D-Ansicht mit ihm beginnt. */
const UEBERBLEND = 4;

function zusammensetzen(z, theta, L, hoehen, Lbauch = L) {
  const { bilder, texZeilen, kTex, profil } = z;
  const n = bilder.length;
  const B = Math.round(Math.abs(L) * kTex);                /* Texturbreite */
  const H = texZeilen;
  /* durchgehender Winkel der Bildmitte in Texturspalten — so skaliert,
     dass eine Umdrehung genau B Spalten hat */
  const mitte = Float64Array.from(theta, (t) => -t * B / Math.abs(L));
  const schritte = Float64Array.from(theta, (_, t) => Math.abs(mitte[Math.min(n - 1, t + 1)] - mitte[Math.max(0, t - 1)]) / 2);
  /* Bereich, den die Streifen tatsächlich abdecken (gewichtet wird
     mit einer Glocke von 0,6 Schritten Breite; was weiter außen liegt,
     zählt kaum) */
  let amin = Infinity, amax = -Infinity;
  for (let t = 0; t < n; t++) {
    if (!bilder[t].streifen) continue;
    const w = Math.min(bilder[t].streifen.w, 2 * Math.max(0.8, 0.6 * schritte[t]));
    amin = Math.min(amin, mitte[t] - w); amax = Math.max(amax, mitte[t] + w);
  }
  /* Struktur je Winkel aus den Streifenmitten — aus dem echten Bild,
     nicht aus dem Messband: dort zählen Spiegelungsreste im Glas so
     viel wie Etikettschrift. */
  const a0 = Math.floor(amin), na = Math.ceil(amax) - a0 + 1;
  const struktur = streifenStruktur(bilder, mitte, a0, na, H);
  const strukturBei = (a) => struktur[Math.max(0, Math.min(na - 1, Math.round(a) - a0))];
  /* Fugenwahl: im Überlappungsbereich die ruhigste Stelle */
  const lo = amin + 8, hi = amax - B - 8;
  let fuge = (lo + hi) / 2;
  if (hi > lo) {
    let best = Infinity;
    for (let a = Math.ceil(lo); a <= hi; a += 1) {
      let e = 0;
      for (let d = -4; d <= 4; d++) e += strukturBei(a + d) + strukturBei(a + B + d);
      if (e < best) { best = e; fuge = a; }
    }
  }
  const malZ = { z, bilder, mitte, schritte, hoehen, H, korrektur: null };
  /* Fuge nachmessen: Anfang und Ende getrennt malen; der Versatz je
     Zeile wird anteilig über den Umlauf verteilt. */
  const fugenVersatz = fugeMessen(malZ, fuge, B);
  if (fugenVersatz) {
    malZ.korrektur = {
      ...fugenVersatz,
      anteil: Float32Array.from(mitte, (m) => Math.max(0, Math.min(1, (m - fuge) / B))),
    };
  }
  const { acc, ws } = malen(malZ, fuge, B, true);
  /* leere Stellen je Zeile aus den Nachbarn */
  let leer = 0;
  for (let c = 0; c < B; c++) {
    let s = 0;
    for (let k = 0; k < H; k++) s += ws[k * B + c];
    if (s <= 1e-6) leer++;
  }
  let daten = new Uint8ClampedArray(B * H * 4);
  for (let k = 0; k < H; k++) {
    const o = k * B;
    let erster = -1;
    for (let c = 0; c < B; c++) if (ws[o + c] > 1e-6) { erster = c; break; }
    for (let c = 0; c < B; c++) {
      let quelle = c;
      if (!(ws[o + c] > 1e-6)) {
        if (erster < 0) continue;
        let l = c, r = c;
        while (!(ws[o + l] > 1e-6)) l = (l - 1 + B) % B;
        while (!(ws[o + r] > 1e-6)) r = (r + 1) % B;
        quelle = (c - l + B) % B <= (r - c + B) % B ? l : r;
      }
      for (let ch = 0; ch < 3; ch++) daten[(o + c) * 4 + ch] = acc[(o + quelle) * 3 + ch] / ws[o + quelle];
      daten[(o + c) * 4 + 3] = 255;
    }
  }
  /* Zeilen auf Höhen der Flasche umrechnen (siehe hoehenAbbildung):
     Bisher folgt jede Zeile der Bildhöhe; das Modell braucht sie
     gleichmäßig über die Flaschenhöhe, von der Mündung bis zum Boden. */
  const abbildung = hoehenAbbildung(z, Lbauch);
  const uEnde = Math.min((H - 1) * z.duTex, abbildung.uBoden);
  const HE = Math.max(16, Math.round(uEnde / z.duTex) + 1);
  const quellZeile = new Float32Array(HE);
  for (let r = 0; r < HE; r++) quellZeile[r] = Math.min(H - 1, abbildung.uBei(r / (HE - 1) * abbildung.anteilBei(uEnde)) / z.duTex);
  const roh = daten;
  daten = new Uint8ClampedArray(B * HE * 4);
  for (let r = 0; r < HE; r++) {
    const q = quellZeile[r], k0 = Math.min(H - 2, Math.floor(q)), f = q - k0;
    for (let c = 0; c < B * 4; c++) daten[r * B * 4 + c] = roh[k0 * B * 4 + c] * (1 - f) + roh[(k0 + 1) * B * 4 + c] * f;
  }
  /* Kante ins leerste Glas drehen, Vorderseite finden */
  const ring = new Float64Array(B);
  for (let c = 0; c < B; c++) ring[c] = strukturBei(fuge + c);
  const schnitt = ruhigsteSpalte(ring);
  const vorneSpalte = vorderseite(daten, B, HE, schnitt);
  const gedreht = new Uint8ClampedArray(B * HE * 4);
  for (let k = 0; k < HE; k++) {
    for (let c = 0; c < B; c++) {
      const q = (k * B + (c + schnitt) % B) * 4, ziel = (k * B + c) * 4;
      gedreht[ziel] = daten[q]; gedreht[ziel + 1] = daten[q + 1]; gedreht[ziel + 2] = daten[q + 2]; gedreht[ziel + 3] = 255;
    }
  }
  return {
    breite: B, hoehe: HE, daten: gedreht,
    vorne: (((vorneSpalte - schnitt) % B) + B) % B / B,
    naht: (((-schnitt) % B) + B) % B,
    fuge: fugenVersatz && { dx: fugenVersatz.mittelDx, dy: fugenVersatz.mittelDy, bloecke: fugenVersatz.bloecke },
    umfang: Math.abs(L), abdeckung: 1 - leer / B,
    profil, form: messForm(profil, z.formSchluessel, abbildung, abbildung.anteilBei(uEnde)),
  };
}

/* Streifen in ein Fenster [von, von + breite) des durchgehenden Winkels
   malen. Zyklisch: Was über den Rand ragt, wird überblendet und auf der
   anderen Seite eingetragen (die Fuge der fertigen Textur). Sonst wird
   es abgeschnitten. Liefert Farbsummen und Gewichte. */
function malen({ z, bilder, mitte, schritte, hoehen, H, korrektur }, von, breite, zyklisch) {
  const { duTex, uMittel } = z;
  const zeilenJeBand = BAND_DU / duTex;
  const acc = new Float32Array(breite * H * 3), ws = new Float32Array(breite * H);
  const versatz = new Float32Array(H), seitlich = new Float32Array(H);
  for (let t = 0; t < bilder.length; t++) {
    const st = bilder[t].streifen;
    if (!st) continue;
    const { w, daten } = st;
    if (mitte[t] + w < von - UEBERBLEND - 4 || mitte[t] - w > von + breite + UEBERBLEND + 4) continue;
    const spalten = 2 * w + 1;
    const sig = Math.max(0.8, 0.6 * schritte[t]);
    const anteil = korrektur ? korrektur.anteil[t] : 0;
    for (let k = 0; k < H; k++) {
      versatz[k] = vz(k * duTex, uMittel, hoehen.vn[t], hoehen.vb[t]) * zeilenJeBand + (korrektur ? korrektur.dy[k] * anteil : 0);
      seitlich[k] = korrektur ? -korrektur.dx[k] * anteil : 0;
    }
    for (let j = -w; j <= w; j++) {
      const gw = Math.exp(-0.5 * (j / sig) ** 2);
      if (gw < 1e-3) continue;
      const basis = mitte[t] + j - von;
      for (let k = 0; k < H; k++) {
        const a = basis + seitlich[k];
        let rand = gw;
        if (zyklisch) {
          if (a < 0) rand *= Math.max(0, 1 + a / UEBERBLEND);
          else if (a >= breite) rand *= Math.max(0, 1 - (a - breite + 1) / UEBERBLEND);
        } else if (a < 0 || a > breite - 1) continue;
        if (rand <= 0) continue;
        const af = zyklisch ? ((a % breite) + breite) % breite : a;
        const c0 = Math.floor(af), fc = af - c0;
        const c1 = zyklisch ? (c0 + 1) % breite : Math.min(breite - 1, c0 + 1);
        const kq = k + versatz[k];
        const k0 = Math.floor(kq), fk = kq - k0;
        if (k0 < 0 || k0 >= H - 1) continue;
        const q0 = (k0 * spalten + j + w) * 3, q1 = q0 + spalten * 3;
        const r = daten[q0] * (1 - fk) + daten[q1] * fk;
        const g = daten[q0 + 1] * (1 - fk) + daten[q1 + 1] * fk;
        const b = daten[q0 + 2] * (1 - fk) + daten[q1 + 2] * fk;
        const w0 = rand * (1 - fc), w1 = rand * fc;
        const o0 = k * breite + c0, o1 = k * breite + c1;
        ws[o0] += w0; acc[o0 * 3] += w0 * r; acc[o0 * 3 + 1] += w0 * g; acc[o0 * 3 + 2] += w0 * b;
        if (w1 > 0) { ws[o1] += w1; acc[o1 * 3] += w1 * r; acc[o1 * 3 + 1] += w1 * g; acc[o1 * 3 + 2] += w1 * b; }
      }
    }
  }
  return { acc, ws };
}

/* Versatz an der Fuge: das Stück um die Fuge einmal aus dem Anfang
   (Fenster bei fuge), einmal aus dem Ende (bei fuge + B) malen und
   gegeneinander verschieben — blockweise in der Höhe, denn der Rest
   hängt von der Höhe ab: Ist der Umriss in einer Höhe um 1 % zu
   schmal gemessen, ist dort der Umlauf um 1 % länger, an der Fuge
   sind das 6 Spalten. dx in Spalten, dy in Zeilen, je Texturzeile:
   Inhalt, der im Anfangsstück bei (c, k) liegt, liegt im Endstück bei
   (c + dx, k + dy). Blöcke ohne deutliches Minimum (glattes Glas)
   werden aus den Nachbarn ergänzt. Null, wenn kein Block trägt. */
function fugeMessen(malZ, fuge, B) {
  const { H } = malZ;
  const R = 12, breite = 2 * R + 1, MAX = 4, SCHRITT = 0.25;
  const hell = ({ acc, ws }) => {
    const g = new Float32Array(breite * H).fill(NaN);
    for (let i = 0; i < g.length; i++) if (ws[i] > 0.05) g[i] = (acc[i * 3] * 0.299 + acc[i * 3 + 1] * 0.587 + acc[i * 3 + 2] * 0.114) / ws[i];
    return g;
  };
  const anfang = hell(malen(malZ, fuge - R, breite, false));
  const ende = hell(malen(malZ, fuge + B - R, breite, false));
  const bei = (bild, x, y) => {
    const x0 = Math.floor(x), y0 = Math.floor(y), fx = x - x0, fy = y - y0;
    if (x0 < 0 || x0 >= breite - 1 || y0 < 0 || y0 >= H - 1) return NaN;
    const o = y0 * breite + x0;
    return (bild[o] * (1 - fx) + bild[o + 1] * fx) * (1 - fy) + (bild[o + breite] * (1 - fx) + bild[o + breite + 1] * fx) * fy;
  };
  const hoeheBlock = Math.max(16, Math.round(H / 20));
  const bloecke = [];
  for (let k0 = 0; k0 < H; k0 += hoeheBlock) {
    const k1 = Math.min(H, k0 + hoeheBlock);
    const kosten = (dx, dy) => {
      let s = 0, m = 0;
      for (let k = k0; k < k1; k++) {
        for (let c = R - 5; c <= R + 5; c++) {
          const a = anfang[k * breite + c], e = bei(ende, c + dx, k + dy);
          if (Number.isNaN(a) || Number.isNaN(e)) continue;
          s += (a - e) ** 2; m++;
        }
      }
      return m > 0.5 * (k1 - k0) * 11 ? s / m : Infinity;
    };
    let best = { dx: 0, dy: 0, k: Infinity };
    const alle = [];
    for (let dy = -MAX; dy <= MAX + 1e-9; dy += SCHRITT) {
      for (let dx = -MAX; dx <= MAX + 1e-9; dx += SCHRITT) {
        const k = kosten(dx, dy);
        if (Number.isFinite(k)) alle.push(k);
        if (k < best.k) best = { dx, dy, k };
      }
    }
    if (!alle.length) continue;
    const med = median(alle);
    const guete = (med - best.k) / (best.k + 4);
    const randlage = Math.abs(best.dx) >= MAX || Math.abs(best.dy) >= MAX;
    if (guete > 1.5 && !randlage) bloecke.push({ k: (k0 + k1 - 1) / 2, dx: best.dx, dy: best.dy, guete });
  }
  if (!bloecke.length) return null;
  /* Ausreißer: weiter als 1,5 vom Median der Nachbarn */
  const gut = bloecke.filter((b, i) => {
    const nb = bloecke.slice(Math.max(0, i - 2), i + 3);
    return Math.abs(b.dx - median(nb.map((x) => x.dx))) <= 1.5 && Math.abs(b.dy - median(nb.map((x) => x.dy))) <= 1.5;
  });
  if (!gut.length) return null;
  const dx = new Float32Array(H), dy = new Float32Array(H);
  for (let k = 0; k < H; k++) {
    let i = 0;
    while (i < gut.length && gut[i].k < k) i++;
    const a = gut[Math.max(0, i - 1)], b = gut[Math.min(gut.length - 1, i)];
    const f = a === b ? 0 : (k - a.k) / (b.k - a.k);
    dx[k] = a.dx + (b.dx - a.dx) * f; dy[k] = a.dy + (b.dy - a.dy) * f;
  }
  return {
    dx, dy, bloecke: gut.length,
    mittelDx: median(gut.map((b) => b.dx)), mittelDy: median(gut.map((b) => b.dy)),
  };
}


/* Struktur je Winkel: für jedes Bild die Helligkeitssprünge in der
   Mittelspalte seines Streifens, gemittelt je ganzer Texturspalte. */
function streifenStruktur(bilder, mitte, a0, na, H) {
  const summe = new Float64Array(na), anzahl = new Float64Array(na);
  for (let t = 0; t < bilder.length; t++) {
    const st = bilder[t].streifen;
    if (!st) continue;
    const { w, daten } = st;
    const sp = 2 * w + 1;
    let e = 0;
    for (let k = 1; k < H; k++) {
      const o = (k * sp + w) * 3, ob = o - sp * 3;
      const hell = daten[o] + daten[o + 1] + daten[o + 2];
      e += Math.abs(hell - (daten[ob] + daten[ob + 1] + daten[ob + 2]))
        + Math.abs(hell - (daten[o + 3] + daten[o + 4] + daten[o + 5]));
    }
    const i = Math.round(mitte[t]) - a0;
    if (i >= 0 && i < na) { summe[i] += e; anzahl[i]++; }
  }
  /* Lücken (schnelle Drehung) aus den Nachbarn */
  const aus = new Float64Array(na);
  let letzter = -1;
  for (let i = 0; i < na; i++) {
    if (anzahl[i]) {
      aus[i] = summe[i] / anzahl[i];
      for (let k = letzter + 1; k < i; k++) aus[k] = letzter < 0 ? aus[i] : aus[letzter] + (aus[i] - aus[letzter]) * (k - letzter) / (i - letzter);
      letzter = i;
    }
  }
  for (let k = letzter + 1; k < na; k++) aus[k] = letzter < 0 ? 0 : aus[letzter];
  return aus;
}

/* Wo die Textur aufgeschnitten wird: das ruhigste Stück Glas. */
function ruhigsteSpalte(ring) {
  const B = ring.length, h = Math.max(2, Math.round(0.02 * B));
  let s = 0, best = 0, bestWert = Infinity;
  for (let d = -h; d <= h; d++) s += ring[((d % B) + B) % B];
  for (let c = 0; c < B; c++) {
    if (s < bestWert) { bestWert = s; best = c; }
    s += ring[(c + h + 1) % B] - ring[(((c - h) % B) + B) % B];
  }
  return best;
}

/* Wo die Vorderseite liegt: Mitte des breitesten Etiketts. Je Spalte
   zählt, wie stark sie sich vom Glas an der Schnittstelle abhebt
   (Farbabstand je Zeile, aufsummiert) — ein Etikett hebt sich über
   seine ganze Breite ab, auch dort, wo keine Schrift ist. Die Läufe
   über der halben Spanne werden über kleine Lücken verbunden; der
   längste gewinnt. */
function vorderseite(daten, B, H, schnitt) {
  const ref = new Float32Array(H * 3);
  for (let k = 0; k < H; k++) {
    for (let d = -3; d <= 3; d++) {
      const o = (k * B + (((schnitt + d) % B) + B) % B) * 4;
      for (let ch = 0; ch < 3; ch++) ref[k * 3 + ch] += daten[o + ch] / 7;
    }
  }
  const abstand = new Float64Array(B);
  for (let c = 0; c < B; c++) {
    let e = 0;
    for (let k = 0; k < H; k++) {
      const o = (k * B + c) * 4;
      e += Math.abs(daten[o] - ref[k * 3]) + Math.abs(daten[o + 1] - ref[k * 3 + 1]) + Math.abs(daten[o + 2] - ref[k * 3 + 2]);
    }
    abstand[c] = e;
  }
  const h = Math.max(1, Math.round(0.01 * B));
  const fein = new Float64Array(B);
  let min = Infinity, max = 0;
  for (let c = 0; c < B; c++) {
    let e = 0;
    for (let d = -h; d <= h; d++) e += abstand[(((c + d) % B) + B) % B];
    fein[c] = e; min = Math.min(min, e); max = Math.max(max, e);
  }
  const schwelle = (min + max) / 2, luecke = Math.round(0.03 * B);
  /* Läufe im Kreis ab dem Schnitt (dort ist es ruhig) */
  const laeufe = [];
  let start = -1;
  for (let k = 0; k <= B; k++) {
    const hoch = k < B && fein[(schnitt + k) % B] >= schwelle;
    if (hoch && start < 0) start = k;
    if (!hoch && start >= 0) { laeufe.push([start, k]); start = -1; }
  }
  const verbunden = [];
  for (const l of laeufe) {
    const letzter = verbunden[verbunden.length - 1];
    if (letzter && l[0] - letzter[1] <= luecke) letzter[1] = l[1];
    else verbunden.push([...l]);
  }
  if (!verbunden.length) return (schnitt + Math.floor(B / 2)) % B;
  let best = verbunden[0];
  for (const l of verbunden) if (l[1] - l[0] > best[1] - best[0]) best = l;
  return (schnitt + Math.floor((best[0] + best[1]) / 2)) % B;
}


const bauchRhoVon = (profil) => (profil.gemessen ? rhoBei(profil, profil.bauch[0]) : 1);

/* ── Von der Bildhöhe zur Flaschenhöhe ────────────────────────
   Die Zeilen der Abwicklung folgen der Bildhöhe; das Modell braucht
   sie gleichmäßig über die Flaschenhöhe. Zwei Dinge verzerren:

   - **Neigung der Kamera**: Schaut sie von oben auf die Flasche, ist
     der Boden weiter weg als die Schulter und erscheint kleiner. Das
     zeigt der Umriss selbst — der Bauch ist ein Zylinder, seine Breite
     im Bild ist also genau der örtliche Maßstab (am Video nimmt sie
     von der Schulter zum Boden um 9 % ab). Eine Zeile ist deshalb
     1 / ρ_Bauch(u) Bauchradien hoch, mit ρ_Bauch der Geraden durch
     den gemessenen Bauch, nach oben verlängert.
   - **Nähe der Kamera**: Die Vorderseite liegt näher als die Kanten
     des Umrisses und erscheint um k = √(x² − 1) / (x − ρ/ρ_Bauch)
     größer (x: Abstand in Bauchradien; am schmalen Hals weniger). Wie
     groß k am Bauch ist, verrät die Drehrate: Die Mitte wandert um
     genau diesen Faktor schneller, als der Umriss vorgibt — der am
     Bauch gemessene Umfang ist k-mal 2π/Δφ. An der gerenderten
     Flasche 1,116 bei wahren 1,115; am Video 1,03 (Kamera also weit
     weg, gut 30 Bauchradien).

   Ohne diese Umrechnung endete die Abwicklung bei naher Kamera ein
   Zehntel über dem Boden (Etiketten auf dem Modell zu hoch), bei
   geneigter Kamera ebenso weit darunter (Teller im Bild). Der Boden
   selbst ist meist verdeckt; die Flasche ist so hoch wie die
   Grundform, in Bauchradien.

   Liefert uBei(a) (Bildzeile zum Anteil a der Flaschenhöhe ab der
   Mündung), anteilBei(u) und uBoden (Bildzeile des Bodens). */
function hoehenAbbildung(z, Lbauch) {
  const { profil } = z;
  const k = Math.max(1.003, Math.min(1.35, Math.abs(Lbauch) / L_NAEHERUNG));
  const x = (k * k + 1) / (k * k - 1);
  const wurzel = Math.sqrt(x * x - 1);
  /* Bauchgerade (ohne Messung: Grundform, ρ = 1) */
  const [b0, b1] = profil.bauch;
  const r0 = profil.gemessen ? rhoBei(profil, b0) : 1, r1 = profil.gemessen ? rhoBei(profil, b1) : 1;
  const steigung = (r1 - r0) / Math.max(1e-6, b1 - b0);
  const bauch = (u) => Math.max(0.3 * r0, r0 + steigung * (u - b0));
  const SCHRITT = 0.01, n = Math.ceil(profil.uBoden * TEXTUR_UEBERHANG / SCHRITT) + 2;
  const tab = new Float64Array(n);           /* Höhe ab Mündung in Bauchradien */
  for (let i = 1; i < n; i++) {
    const u = (i - 0.5) * SCHRITT;
    const rb = bauch(u), rel = Math.min(1, rhoBei(profil, u) / rb);
    tab[i] = tab[i - 1] + SCHRITT / (rb * wurzel / (x - rel));
  }
  const hoehe = profil.uBoden;
  const anteilBei = (u) => {
    const i = Math.max(0, Math.min(n - 2, Math.floor(u / SCHRITT))), f = u / SCHRITT - i;
    return (tab[i] * (1 - f) + tab[i + 1] * f) / hoehe;
  };
  const uBei = (a) => {
    const ziel = a * hoehe;
    let lo = 0, hi = n - 1;
    if (tab[hi] < ziel) return hi * SCHRITT;
    while (hi - lo > 1) { const m = (lo + hi) >> 1; if (tab[m] < ziel) lo = m; else hi = m; }
    const d = tab[hi] - tab[lo];
    return (lo + (d > 1e-12 ? (ziel - tab[lo]) / d : 0)) * SCHRITT;
  };
  return { uBei, anteilBei, uBoden: uBei(1), x, k };
}

/* Drehrate am Bauch gegenüber allen Bandzeilen: Paare mit großer
   Verschiebung noch einmal nur mit den Bauchzeilen gemessen. */
function bauchRate(z, bands, still, theta, mess) {
  const { gruppen, nz } = z;
  const ab = gruppen[1][0], nb = gruppen[1].length;
  if (!(nb >= 8)) return 1;
  const kandidaten = mess.filter(([a, b, s, q]) => Math.abs(s) >= 6 && q > 1 && b - a >= 4);
  const schritt = Math.max(1, Math.floor(kandidaten.length / 150));
  let summeAlle = 0, summeBauch = 0;
  for (let i = 0; i < kandidaten.length; i += schritt) {
    const [a, b, s] = kandidaten[i];
    const t = vergleicheRest(bands[a].subarray(ab * NPHI), bands[b].subarray(ab * NPHI), nb,
      Math.floor(s) - 3, Math.ceil(s) + 3, 2, still.subarray(ab * NPHI));
    if (t.amRand) continue;
    summeAlle += Math.abs(s); summeBauch += Math.abs(t.s);
  }
  void nz; void theta;
  return summeAlle > 50 ? Math.max(0.9, Math.min(1.1, summeBauch / summeAlle)) : 1;
}

/** Gemessene Form für das 3D-Modell: oben wie gemessen (Mündung, Hals,
    Schulter), der Bauch zylindrisch — die Verjüngung im Bild war
    Perspektive, nicht Glas. Maße auf den Bauchradius der Grundform
    bezogen, damit Kapseldicken und Ähnliches passen; die Höhen folgen
    der Abbildung von Bild- auf Flaschenhöhe (bis zum Anteil `bis`, so
    weit wie die Abwicklung reicht). Den Boden verdecken Teller und
    Hand, er wird wie bei der Grundform gerundet. */
function messForm(profil, formSchluessel, abbildung = null, bis = 1) {
  const basis = formVon(formSchluessel);
  if (!profil.gemessen) return basis;
  const Rk = koerperRadius(basis);
  const rhoB = bauchRhoVon(profil);
  const hoehe = Math.round(profil.uBoden * Rk * bis * 10) / 10;
  const uBei = abbildung ? abbildung.uBei : (a) => a * profil.uBoden;
  const punkte = [];
  const SCHRITTE = 64;
  for (let k = SCHRITTE; k >= 0; k--) {
    const a = k / SCHRITTE;
    const u = uBei(a * bis);
    const h = Math.max(0, hoehe * (1 - a));
    let r = u >= profil.bauch[0] ? Rk : Math.min(Rk, rhoBei(profil, u) / rhoB * Rk);
    if (h < 9) r = Rk * radiusAnteil(basis, h);
    punkte.push([Math.round(h * 10) / 10, Math.round(r * 10) / 10]);
  }
  punkte[0][0] = 0;
  punkte[punkte.length - 1][0] = hoehe;
  return { name: basis.name, hoehe, kapsel: basis.kapsel, profil: punkte, gemessen: true };
}

const radiusAnteil = (form, h) => {
  const Rk = koerperRadius(form);
  const p = form.profil;
  for (let i = 1; i < p.length; i++) {
    if (h <= p[i][0]) return (p[i - 1][1] + (p[i][1] - p[i - 1][1]) * (h - p[i - 1][0]) / (p[i][0] - p[i - 1][0])) / Rk;
  }
  return 1;
};


/* ── Kapsel finden ────────────────────────────────────────────
   Die Kapsel sitzt oben am Hals und hebt sich farblich vom Glas ab.
   Gesucht wird ihre Unterkante zwischen Mündung und Halsansatz. Für
   jede Zeile zählt der Unterschied zwischen ALLEM darüber (der Kapsel
   als Ganzes) und dem kurzen Stück darunter — so stört weder ein Ring
   in der Kapsel noch eine helle Halsschleife darunter. Dazu ein
   weicher Vorzug für die übliche Länge der Form. Ist kein Sprung
   deutlich, gilt die übliche Länge, als unsicher markiert. */
function kapselErkennen(bild, formAngabe) {
  const form = typeof formAngabe === "string" || !formAngabe ? formVon(formAngabe) : formAngabe;
  const W = bild.breite || bild.width, H = bild.hoehe || bild.height, data = bild.daten || bild.data;
  const zeilen = new Float32Array(H * 3);
  for (let y = 0; y < H; y++) {
    let r = 0, g = 0, b = 0;
    for (let x = 0; x < W; x++) { const i = (y * W + x) * 4; r += data[i]; g += data[i + 1]; b += data[i + 2]; }
    zeilen[y * 3] = r / W; zeilen[y * 3 + 1] = g / W; zeilen[y * 3 + 2] = b / W;
  }
  const yVonH = (h) => Math.round((1 - h / form.hoehe) * (H - 1));
  const standard = yVonH(form.hoehe * (1 - form.kapsel));
  const oben = Math.max(2, Math.round(0.035 * H));
  const unten = Math.min(H - 3, yVonH(kapselTiefste(form)));
  const fenster = Math.max(2, Math.round(0.02 * H));
  const mittel = (a, e) => {
    const m = [0, 0, 0];
    const lo = Math.max(0, a), hi = Math.min(H, e);
    for (let y = lo; y < hi; y++) for (let c = 0; c < 3; c++) m[c] += zeilen[y * 3 + c];
    const n = Math.max(1, hi - lo);
    return m.map((v) => v / n);
  };
  const streuung = 0.08 * H;
  let best = standard, bestWert = -1, bestSprung = 0;
  for (let y = oben + fenster; y <= unten - fenster; y++) {
    const a = mittel(oben, y), b = mittel(y, y + fenster);
    const sprung = Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2]);
    const naehe = Math.exp(-0.5 * ((y - standard) / streuung) ** 2);
    if (sprung * naehe > bestWert) { bestWert = sprung * naehe; best = y; bestSprung = sprung; }
  }
  const sicher = bestSprung >= 18;
  const kante = sicher ? best : standard;
  const farbe = mittel(oben, Math.max(oben + 1, kante - fenster)).map((v) => Math.round(v));
  void halsBeginn;
  return { anteil: kante / (H - 1), farbe, sicher };
}


export {
  DPHI, NPHI, abwicklungAuswerten, abwicklungBeginnen, abwicklungSchritt, aufnahmeStarten, ausgleich,
  bandAbtasten, festesBild, glaetten, kapselErkennen, messForm, ruhigsteSpalte, schichtenTrennen, vergleicheRest, vorderseite, vorschauSchritt,
};
